import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, ArrowLeft, RefreshCw, Loader2, FileText, Layers, Calendar, Upload, X, Image, ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import { format } from "date-fns";

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

export default function TraceabilityPage() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const isDeck = type === "deck";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  
  // Feedback state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Editable fields for single slides
  const [editContent, setEditContent] = useState("");
  const [contentImages, setContentImages] = useState([]); // { id, thumbnail_url, filename }
  const [inspirationFiles, setInspirationFiles] = useState([]); // { id, thumbnail_url, filename }
  const contentImageRef = useRef(null);
  const inspirationRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = isDeck ? `${API}/decks/${id}` : `${API}/presentations/${id}`;
        const res = await axios.get(endpoint);
        setData(res.data);
        if (!isDeck) {
          setEditContent(res.data.inputs?.content || res.data.content || "");
          // Fetch actual file info for content images
          const cids = res.data.content_image_ids || res.data.inputs?.content_image_ids || [];
          const iids = res.data.inspiration_file_ids || res.data.inputs?.inspiration_file_ids || [];
          const fetchFiles = async (ids) => {
            const files = [];
            for (const fid of ids) {
              try {
                const r = await axios.get(`${API}/files/${fid}`);
                files.push(r.data);
              } catch { files.push({ id: fid, filename: fid, thumbnail_url: null }); }
            }
            return files;
          };
          setContentImages(await fetchFiles(cids));
          setInspirationFiles(await fetchFiles(iids));
        }
      } catch { toast.error("Failed to load details"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id, isDeck]);

  const handleDownload = () => {
    // Let the server's Content-Disposition pick the slugified <title>.pptx.
    const a = document.createElement("a");
    a.href = isDeck ? `${API}/download-deck/${id}` : `${API}/download/${id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      if (isDeck) {
        await axios.post(`${API}/decks/${id}/retry`);
      } else {
        await axios.post(`${API}/generate`, {
          content: editContent.trim(),
          content_image_ids: contentImages.map(f => f.id),
          inspiration_file_ids: inspirationFiles.map(f => f.id),
          template_id: data.template_id || undefined,
        });
      }
      toast.success("Regenerating...");
      navigate("/history");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to regenerate"); }
    finally { setRegenerating(false); }
  };

  const handleUploadContentImage = async (e) => {
    for (const file of Array.from(e.target.files || [])) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("file_type", "content_image");
        const res = await axios.post(`${API}/upload-file`, fd);
        setContentImages(prev => [...prev, res.data]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    e.target.value = "";
  };

  const handleUploadInspiration = async (e) => {
    for (const file of Array.from(e.target.files || [])) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("file_type", "inspiration");
        const res = await axios.post(`${API}/upload-file`, fd);
        setInspirationFiles(prev => [...prev, res.data]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    e.target.value = "";
  };

  const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy h:mm a"); } catch { return d || "—"; } };

  const handleFeedback = async (rating) => {
    try {
      const endpoint = isDeck 
        ? `${API}/decks/${id}/feedback` 
        : `${API}/presentations/${id}/feedback`;
      await axios.post(endpoint, { 
        rating,
        feedback_text: data.feedback?.feedback_text 
      }, { withCredentials: true });
      
      // Update local state
      setData(prev => ({
        ...prev,
        feedback: { ...prev.feedback, rating, created_at: new Date().toISOString() }
      }));
      toast.success("Thanks for your feedback!");
    } catch { 
      toast.error("Failed to submit feedback"); 
    }
  };

  const handleSubmitFeedbackText = async () => {
    setSubmittingFeedback(true);
    try {
      const endpoint = isDeck 
        ? `${API}/decks/${id}/feedback` 
        : `${API}/presentations/${id}/feedback`;
      await axios.post(endpoint, { 
        rating: data.feedback?.rating,
        feedback_text: feedbackText 
      }, { withCredentials: true });
      
      // Update local state
      setData(prev => ({
        ...prev,
        feedback: { ...prev.feedback, feedback_text: feedbackText, created_at: new Date().toISOString() }
      }));
      setShowFeedbackDialog(false);
      toast.success("Feedback saved!");
    } catch { 
      toast.error("Failed to save feedback"); 
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) return <div className="page-container flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="page-container"><p className="text-gray-500">Not found</p></div>;

  const slides = data.slides || [];
  const status = data.status || "complete";
  const canDownload = status === "complete" && data.file_path;

  return (
    <div className="page-container" data-testid="traceability-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
        <button onClick={() => navigate("/history")} className="hover:text-gray-600 flex items-center gap-1" data-testid="back-btn"><ArrowLeft className="w-3.5 h-3.5" />History</button>
        <span>/</span><span className="text-gray-900">Details</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold font-['Manrope']" data-testid="page-title">{isDeck ? "Deck Details" : "Slide Details"}</h1>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${isDeck ? "text-indigo-700 bg-indigo-50 border border-indigo-200" : "text-gray-600 bg-gray-100 border border-gray-200"}`}>
            {isDeck ? <><Layers className="w-3 h-3" />Deck</> : <><FileText className="w-3 h-3" />Slide</>}
          </span>
          <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${status === "complete" ? "text-green-700 bg-green-50 border border-green-200" : status === "failed" ? "text-red-600 bg-red-50 border border-red-200" : "text-amber-600 bg-amber-50 border border-amber-200"}`} data-testid="status-badge">
            {capitalize(status)}
          </span>
          {data.template_name && (
            <span className="text-xs text-gray-500 bg-gray-50 rounded-full px-2.5 py-1 border border-gray-200">{data.template_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canDownload && (
            <Button size="sm" onClick={handleDownload} className="bg-black hover:bg-gray-800" data-testid="download-btn">
              <Download className="w-3.5 h-3.5 mr-1" />Download PPTX
            </Button>
          )}
        </div>
      </div>

      {/* Metadata bar */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(data.created_at)}</div>
        {data.completed_at && <div>Completed: {fmtDate(data.completed_at)}</div>}
        {isDeck && <div>{data.num_slides || slides.length} slides</div>}
      </div>

      {/* User Feedback Section - Interactive */}
      {status === "complete" && (
        <Card className="border-gray-200 shadow-none mb-6" data-testid="feedback-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              Your Feedback
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-gray-500">Rate this {isDeck ? "deck" : "slide"}:</span>
              <button
                onClick={() => handleFeedback("like")}
                className={`p-2 rounded-lg transition-colors ${
                  data.feedback?.rating === "like" 
                    ? "bg-green-100 text-green-600 border border-green-200" 
                    : "hover:bg-gray-100 text-gray-400 border border-gray-200"
                }`}
                data-testid="feedback-like-btn"
              >
                <ThumbsUp className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleFeedback("dislike")}
                className={`p-2 rounded-lg transition-colors ${
                  data.feedback?.rating === "dislike" 
                    ? "bg-red-100 text-red-600 border border-red-200" 
                    : "hover:bg-gray-100 text-gray-400 border border-gray-200"
                }`}
                data-testid="feedback-dislike-btn"
              >
                <ThumbsDown className="w-5 h-5" />
              </button>
              <button
                onClick={() => { setFeedbackText(data.feedback?.feedback_text || ""); setShowFeedbackDialog(true); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  data.feedback?.feedback_text 
                    ? "bg-blue-100 text-blue-600 border border-blue-200" 
                    : "hover:bg-gray-100 text-gray-500 border border-gray-200"
                }`}
                data-testid="feedback-comment-btn"
              >
                <MessageSquare className="w-4 h-4" />
                {data.feedback?.feedback_text ? "Edit Comment" : "Add Comment"}
              </button>
            </div>
            
            {/* Show existing feedback */}
            {data.feedback?.feedback_text && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 italic border border-gray-100">
                "{data.feedback.feedback_text}"
              </div>
            )}
            {data.feedback?.created_at && (
              <p className="text-xs text-gray-400 mt-2">
                Last updated: {fmtDate(data.feedback.created_at)}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main content area */}
      {isDeck ? (
        <DeckView data={data} slides={slides} status={status} handleDownload={handleDownload} handleRegenerate={handleRegenerate} regenerating={regenerating} />
      ) : (
        <SlideView
          data={data}
          editContent={editContent}
          setEditContent={setEditContent}
          contentImages={contentImages}
          setContentImages={setContentImages}
          inspirationFiles={inspirationFiles}
          setInspirationFiles={setInspirationFiles}
          contentImageRef={contentImageRef}
          inspirationRef={inspirationRef}
          handleUploadContentImage={handleUploadContentImage}
          handleUploadInspiration={handleUploadInspiration}
          handleRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
      )}

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave a Comment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="What could be improved? Any specific issues or suggestions?"
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              className="min-h-[120px]"
              data-testid="feedback-text-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitFeedbackText} disabled={submittingFeedback} className="bg-teal-600 hover:bg-teal-700">
              {submittingFeedback ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Comment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FileThumbnailGrid({ files, setFiles, inputRef, onUpload, label, testPrefix }) {
  return (
    <Card className="border-gray-200 shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Image className="w-3.5 h-3.5 text-gray-500" />{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <input ref={inputRef} type="file" multiple accept="image/*,.pdf,.pptx" onChange={onUpload} className="hidden" />
        {files.length > 0 ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div key={f.id} className="relative group">
                  {f.thumbnail_url ? (
                    <img src={f.thumbnail_url} alt={f.filename || `File ${i+1}`} className="w-16 h-12 rounded object-cover border border-gray-200" />
                  ) : (
                    <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center text-[9px] text-gray-400 border" title={f.filename}>
                      <FileText className="w-4 h-4 text-gray-300" />
                    </div>
                  )}
                  <button
                    onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 hover:bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`${testPrefix}-remove-${i}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => inputRef.current?.click()} className="text-xs text-gray-500 hover:text-gray-700" data-testid={`${testPrefix}-add`}>+ Add more</button>
          </div>
        ) : (
          <button onClick={() => inputRef.current?.click()} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-md text-xs text-gray-500 hover:border-gray-300 hover:bg-gray-50 transition-colors" data-testid={`${testPrefix}-upload`}>
            <Upload className="w-4 h-4 mx-auto mb-1" />Upload
          </button>
        )}
      </CardContent>
    </Card>
  );
}

function SlideView({ data, editContent, setEditContent, contentImages, setContentImages, inspirationFiles, setInspirationFiles, contentImageRef, inspirationRef, handleUploadContentImage, handleUploadInspiration, handleRegenerate, regenerating }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* LEFT: Preview — 60-70% */}
      <div className="lg:col-span-3 space-y-3">
        <Card className="border-gray-200 shadow-none overflow-hidden">
          <div className="aspect-video bg-gray-50 flex items-center justify-center" data-testid="slide-preview">
            {data.id ? (
              <img 
                src={`${API}/thumbnails/${data.id}`} 
                alt="Slide Preview" 
                className="w-full h-full object-contain" 
                data-testid="slide-preview-img"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div className="text-gray-300 flex-col items-center" style={{ display: data.id ? 'none' : 'flex' }}>
              <FileText className="w-16 h-16 mb-2" />
              <span className="text-sm">No preview available</span>
            </div>
          </div>
        </Card>
      </div>

      {/* RIGHT: Editable inputs — 30-40% */}
      <div className="lg:col-span-2 space-y-4">
        {/* Slide Content + Regenerate */}
        <Card className="border-gray-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Slide Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="min-h-[140px] border-gray-200 text-sm"
              placeholder="Slide content..."
              data-testid="edit-content"
            />
            <Button
              onClick={handleRegenerate}
              disabled={regenerating || !editContent.trim()}
              className="w-full bg-black hover:bg-gray-800"
              data-testid="regenerate-btn"
            >
              {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Regenerate
            </Button>
          </CardContent>
        </Card>

        {/* Content Images with actual thumbnails */}
        <FileThumbnailGrid
          files={contentImages}
          setFiles={setContentImages}
          inputRef={contentImageRef}
          onUpload={handleUploadContentImage}
          label="Content Images"
          testPrefix="content-img"
        />

        {/* Style References with actual thumbnails */}
        <FileThumbnailGrid
          files={inspirationFiles}
          setFiles={setInspirationFiles}
          inputRef={inspirationRef}
          onUpload={handleUploadInspiration}
          label="Style References"
          testPrefix="style-ref"
        />
      </div>
    </div>
  );
}

function DeckView({ data, slides, status, handleDownload, handleRegenerate, regenerating }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Build thumbnail URLs using slide IDs
  const slideIds = data.slide_ids || slides.map(s => s.id);
  const getSlideThumbnailUrl = (index) => {
    const slideId = slideIds[index] || slides[index]?.id;
    return slideId ? `${API}/thumbnails/${slideId}` : null;
  };

  const handleSlideDownload = (slide) => {
    const a = document.createElement("a");
    a.href = `${API}/download/${slide.id}`;
    a.download = `slide_${slide.slide_index || 0}.pptx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* LEFT: Slide preview — 60-70% */}
      <div className="lg:col-span-3 space-y-3">
        <Card className="border-gray-200 shadow-none overflow-hidden">
          <div className="aspect-video bg-gray-50 flex items-center justify-center" data-testid="deck-preview">
            {slideIds.length > 0 ? (
              <img 
                src={getSlideThumbnailUrl(currentSlide)} 
                alt="Deck Preview" 
                className="w-full h-full object-contain" 
                data-testid="deck-preview-img"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div className="text-gray-300 flex-col items-center" style={{ display: slideIds.length > 0 ? 'none' : 'flex' }}>
              <Layers className="w-16 h-16 mb-2" />
              <span className="text-sm">No preview available</span>
            </div>
          </div>
        </Card>
        {/* Slide thumbnails strip */}
        {slideIds.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {slideIds.map((sid, i) => (
              <button key={sid || i} onClick={() => setCurrentSlide(i)}
                className={`flex-shrink-0 w-20 aspect-video rounded overflow-hidden border-2 transition-colors ${currentSlide === i ? "border-black" : "border-gray-200 hover:border-gray-400"}`}
                data-testid={`slide-thumb-${i}`}
              >
                <img src={`${API}/thumbnails/${sid}`} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT: Deck info */}
      <div className="lg:col-span-2 space-y-4">
        <Card className="border-gray-200 shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Topic</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-gray-700" data-testid="deck-topic">{data.inputs?.topic || data.topic || "—"}</p></CardContent>
        </Card>

        {data.storyline && (
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Storyline</CardTitle></CardHeader>
            <CardContent>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 rounded-md p-3 max-h-48 overflow-y-auto" data-testid="deck-storyline">{data.storyline}</pre>
            </CardContent>
          </Card>
        )}

        {/* Slides list with individual download */}
        {slides.length > 0 && (
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Slides ({slides.length})</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
              {slides.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 p-1.5 rounded bg-gray-50 hover:bg-gray-100 transition-colors group">
                  <button onClick={() => setCurrentSlide(i)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                    <div className="w-12 h-8 rounded bg-gray-200 flex-shrink-0 flex items-center justify-center overflow-hidden text-[9px] text-gray-400">
                      {s.id ? <img src={`${API}/thumbnails/${s.id}`} alt="" className="w-full h-full object-cover" /> : `S${i+1}`}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 line-clamp-1">{s.content?.slice(0, 60) || `Slide ${i+1}`}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`text-[10px] font-medium ${s.status === "complete" ? "text-green-600" : s.status === "failed" ? "text-red-500" : "text-amber-500"}`}>{capitalize(s.status)}</span>
                    {s.status === "complete" && s.file_path && (
                      <button
                        onClick={() => handleSlideDownload(s)}
                        className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-all"
                        title="Download this slide"
                        data-testid={`download-slide-${i}`}
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Regenerate button for decks */}
        <Button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="w-full bg-black hover:bg-gray-800"
          data-testid="regenerate-btn"
        >
          {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Regenerate Deck
        </Button>
      </div>
    </div>
  );
}
